import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';
import { SelectField, useFormContext } from '@/components/form';
import { AreaSearchField } from './AreaSearchField';
import { AreaDialog } from './AreaDialog';

interface Props {
  projectId: string;
  roomOptions: { value: string; label: string }[];
  roomFieldName?: string;
  areaFieldName?: string;
}

/**
 * The paired Room + Area row shared by every item dialog. Renders the existing
 * Room `SelectField` next to the {@link AreaSearchField} (Client-style combobox
 * with a "+ New area" row) and hosts the nested {@link AreaDialog} used to create
 * an area inline. Must be rendered inside a `<Form>` (reads context via
 * `useFormContext`). The Area field is scoped to the currently-selected room and
 * self-clears when the room changes.
 */
export function RoomAreaFields({
  projectId,
  roomOptions,
  roomFieldName = 'roomId',
  areaFieldName = 'areaId',
}: Props) {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const roomId = useWatch({ control, name: roomFieldName }) as string | undefined;
  const [areaDialogOpen, setAreaDialogOpen] = useState(false);

  return (
    <div className="grid grid-cols-2 gap-3">
      <SelectField
        name={roomFieldName}
        label={t('projects.rooms.selectLabel')}
        options={roomOptions}
        onValueChange={() => setValue(areaFieldName, '', { shouldDirty: true })}
      />
      <AreaSearchField
        name={areaFieldName}
        label={t('projects.areas.selectLabel')}
        projectId={projectId}
        roomId={roomId || undefined}
        onCreateNew={() => setAreaDialogOpen(true)}
      />
      {roomId ? (
        <AreaDialog
          projectId={projectId}
          roomId={roomId}
          area={null}
          open={areaDialogOpen}
          onClose={() => setAreaDialogOpen(false)}
          onCreated={(area) =>
            setValue(areaFieldName, area.id, { shouldDirty: true, shouldValidate: true })
          }
        />
      ) : null}
    </div>
  );
}
